import {FC} from 'react'; // Add ChangeEvent import
import {Sparkles, Wand2, Plus, Database, Layers, Stars} from 'lucide-react';
import {Link} from 'react-router-dom';
import {BackgroundGradient} from '@/components/ui/background-gradient';
import {Button} from '@/components/ui/button.tsx';
import {Checkbox} from '@/components/ui/checkbox.tsx';

interface GenerateMoreShredsCardProps {
    id: string;
    serviceId: string;
    loading: boolean;
    generateShredModel: boolean;
    setGenerateShredModel: (value: boolean) => void;
    onGenerateShred: () => void;
    hasDataModel: boolean;
    disabled?: boolean; // Add this line
}

const GenerateMoreShredsCard: FC<GenerateMoreShredsCardProps> = ({
                                                                     id,
                                                                     serviceId,
                                                                     loading,
                                                                     generateShredModel,
                                                                     setGenerateShredModel,
                                                                     onGenerateShred,
                                                                     hasDataModel,
                                                                     disabled = false // Add this line with default value
                                                                 }) => {
    return (
        <div className="relative">
            <div
                className={`relative overflow-hidden rounded-2xl min-h-[300px] col-span-12 lg:col-span-6 bg-gradient-to-br from-indigo-500/10 via-purple-500/10 to-pink-500/10 flex flex-col border border-indigo-300 shadow-sm hover:shadow-lg transition-all duration-300 hover:border-indigo-400 group ${
                    disabled ? 'opacity-60 cursor-not-allowed' : ''
                }`}>

                {/* Enhanced AI Background Effects */}
                <div className="absolute inset-0 overflow-hidden">
                    {/* Animated gradient orbs */}
                    <div
                        className="absolute top-8 right-8 w-16 h-16 bg-gradient-to-br from-indigo-400/30 to-purple-400/30 rounded-full blur-xl animate-pulse"></div>
                    <div
                        className="absolute bottom-8 left-8 w-12 h-12 bg-gradient-to-br from-purple-400/30 to-pink-400/30 rounded-full blur-xl animate-pulse delay-1000"></div>
                    <div
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-2xl animate-pulse delay-500"></div>

                    {/* Floating particles */}
                    <div
                        className="absolute top-12 left-12 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-200"></div>
                    <div
                        className="absolute top-20 right-16 w-1 h-1 bg-purple-400 rounded-full animate-bounce delay-700"></div>
                    <div
                        className="absolute bottom-16 right-12 w-2 h-2 bg-pink-400 rounded-full animate-bounce delay-1200"></div>
                </div>

                {/* Status Indicator - Top Right */}
                <div className="absolute top-4 right-4 z-10">
                    <div
                        className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-full text-xs font-medium border backdrop-blur-sm ${
                            disabled
                                ? 'bg-gray-100 text-gray-500 border-gray-300'
                                : 'bg-gradient-to-r from-indigo-500/20 to-purple-500/20 text-indigo-700 border-indigo-300'
                        }`}>
                        <div className={`w-2 h-2 rounded-full ${
                            disabled
                                ? 'bg-gray-400'
                                : 'bg-gradient-to-r from-indigo-500 to-purple-500 animate-pulse'
                        }`}/>
                        <Stars className="w-3 h-3"/>
                        <span>{disabled ? 'Disabled' : 'AI Powered'}</span>
                    </div>
                </div>

                <div className="relative z-10 p-6 flex flex-col h-full">
                    {/* Top Section - Title */}
                    <div className="mb-6 text-center">
                        <div
                            className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 shadow-lg relative overflow-hidden transition-transform duration-500 ${
                                disabled
                                    ? 'bg-gray-400 group-hover:scale-100'
                                    : 'bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 group-hover:scale-110'
                            }`}>
                            {/* Shimmer effect - only show when not disabled */}
                            {!disabled && (
                                <div
                                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 animate-shimmer"></div>
                            )}
                            <Wand2 className="w-8 h-8 text-white relative z-10"/>
                        </div>
                        <h1 className={`text-xl font-semibold text-center leading-tight ${
                            disabled
                                ? 'text-gray-500'
                                : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent'
                        }`}>
                            Generate More Shreds
                        </h1>
                    </div>

                    {/* Middle Section - Information */}
                    <div className="flex-1 grid grid-cols-12 gap-4 mb-6">
                        {/* Icon Section */}
                        <div className="col-span-12 lg:col-span-3">
                            <div
                                className={`backdrop-blur-sm border rounded-xl flex justify-center items-center py-4 h-full transition-colors ${
                                    disabled
                                        ? 'bg-gray-100 border-gray-200'
                                        : 'bg-white/60 border-indigo-200 hover:bg-white/80'
                                }`}>
                                <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg ${
                                    disabled
                                        ? 'bg-gray-400'
                                        : 'bg-gradient-to-br from-indigo-500 to-purple-600'
                                }`}>
                                    <Plus className="w-7 h-7 text-white"/>
                                </div>
                            </div>
                        </div>

                        {/* Information Section */}
                        <div className="col-span-12 lg:col-span-9 flex flex-col gap-3">
                            {/* AI Features */}
                            <div className={`rounded-xl p-4 border transition-all ${
                                disabled
                                    ? 'bg-gray-50 border-gray-200'
                                    : 'bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200 hover:shadow-sm'
                            }`}>
                                <div className="flex items-start space-x-3">
                                    <div
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            disabled
                                                ? 'bg-gray-400'
                                                : 'bg-gradient-to-br from-indigo-500 to-indigo-600'
                                        }`}>
                                        <Sparkles className="w-4 h-4 text-white"/>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className={`text-sm font-semibold mb-1 ${
                                            disabled ? 'text-gray-500' : 'text-indigo-800'
                                        }`}>
                                            {disabled ? 'Requires Model Validation' : 'Expand Your Service'}
                                        </h4>
                                        <p className={`text-xs leading-relaxed ${
                                            disabled ? 'text-gray-400' : 'text-indigo-600'
                                        }`}>
                                            {disabled
                                                ? 'Validate your data model first to unlock this feature'
                                                : 'Add more intelligent modules to enhance functionality'
                                            }
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className={`rounded-xl p-4 border transition-all ${
                                disabled
                                    ? 'bg-gray-50 border-gray-200'
                                    : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:shadow-sm'
                            }`}>
                                <div className="flex items-start space-x-3">
                                    <div
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                            disabled
                                                ? 'bg-gray-400'
                                                : 'bg-gradient-to-br from-purple-500 to-purple-600'
                                        }`}>
                                        <Layers className="w-4 h-4 text-white"/>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className={`text-sm font-semibold mb-1 ${
                                            disabled ? 'text-gray-500' : 'text-purple-800'
                                        }`}>Smart Architecture</h4>
                                        <p className={`text-xs leading-relaxed ${
                                            disabled ? 'text-gray-400' : 'text-purple-600'
                                        }`}>
                                            AI analyzes your existing modules for optimal additions
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Model Checkbox */}
                    {!hasDataModel && (
                        <div className="mb-4">
                            <div className={`rounded-xl p-3 border ${
                                disabled
                                    ? 'bg-gray-50 border-gray-200'
                                    : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
                            }`}>
                                <label
                                    className={`flex items-center space-x-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <Checkbox
                                        checked={generateShredModel}
                                        onCheckedChange={(checked) => !disabled && setGenerateShredModel(checked as boolean)}
                                        color="indigo-500"
                                        disabled={disabled}
                                    />
                                    <div className="flex items-center space-x-2">
                                        <Database className={`w-4 h-4 ${disabled ? 'text-gray-400' : 'text-blue-600'}`} />
                                        <span className={`text-sm font-medium ${disabled ? 'text-gray-500' : 'text-blue-800'}`}>Include Data Model</span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Separator Line */}
                    <div className={`h-px bg-gradient-to-r from-transparent to-transparent mb-6 ${
                        disabled ? 'via-gray-200' : 'via-indigo-200'
                    }`} />

                    {/* Bottom Section - Action Buttons */}
                    <div className="grid grid-cols-2 gap-3 mt-auto">
                        {/* Manual Creation Link */}
                        <Link
                            to={disabled ? '#' : `/architecture/project/${id}/services/${serviceId}/create`}
                            className={`text-sm py-3 px-4 rounded-xl font-medium flex items-center justify-center w-full transition-colors no-underline ${
                                disabled 
                                    ? 'border border-gray-200 text-gray-400 cursor-not-allowed'
                                    : 'border border-indigo-200 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-300'
                            }`}
                            onClick={disabled ? (e) => e.preventDefault() : undefined}
                        >
                            Add Manually
                        </Link>

                        {/* AI Generation Button */}
                        <BackgroundGradient animate={!disabled} className="rounded-xl p-[1px]">
                            <Button
                                size="default"
                                variant="ghost"
                                className="w-full bg-transparent text-white hover:bg-transparent flex items-center justify-center py-3 text-sm font-bold relative overflow-hidden"
                                onClick={onGenerateShred}
                                loading={loading}
                                disabled={loading || disabled}
                            >
                                <Sparkles className="w-4 h-4 mr-2" />
                                AI Generate
                            </Button>
                        </BackgroundGradient>
                    </div>
                </div>

                {/* Hover glow effect - only when not disabled */}
                {!disabled && (
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl"></div>
                )}
            </div>

            {/* Shimmer animation styles */}
            <style>{`
                @keyframes shimmer {
                    0% {
                        transform: translateX(-100%) skewX(-12deg);
                    }
                    100% {
                        transform: translateX(200%) skewX(-12deg);
                    }
                }
                
                .animate-shimmer {
                    animation: shimmer 2s infinite;
                }
            `}</style>
        </div>
    );
};

export default GenerateMoreShredsCard;